import type { Program } from '@/types'

/**
 * Preview-only stand-in for the real AI generator (Phase 5 will call the
 * `ai-proxy` Netlify function → Claude, returning this same structured shape).
 * Here we synthesise a plausible program from keywords so the coach can see the
 * end-to-end "describe → structured program → edit → save" flow.
 */
export function mockGenerate(prompt: string): Program {
  const p = prompt.toLowerCase()
  const isRehab = /acl|rehab|return|injur|knee|hamstring/.test(p)
  const isJump = /jump|vertical|dunk|explos|power/.test(p)
  const days = (p.match(/(\d)\s*day/) || [])[1]
  const dayCount = days ? Math.min(Math.max(Number(days), 1), 5) : isRehab ? 2 : 3

  const name = isRehab
    ? 'ACL Return to Play — Phase 3'
    : isJump
    ? 'Vertical Power Block'
    : 'Strength Development Block'

  const description = isRehab
    ? 'Progressive post-injury loading with symmetry priority. Avoids deep loaded knee flexion.'
    : isJump
    ? 'Max strength into rate-of-force-development for jump performance.'
    : 'Foundational strength progression across the main lifts.'

  const tags = isRehab ? ['rehab', 'acl'] : isJump ? ['power', 'vertical'] : ['strength']

  const dayTemplate = (i: number) => {
    if (isRehab) {
      return {
        label: `Day ${i + 1}`,
        focus: i === 0 ? 'Controlled Strength' : 'Posterior + Trunk',
        exercises: [
          { exercise_id: 'warmup', name: 'Bike + Mobility', reps: '10 min', is_warmup: true },
          { exercise_id: 'leg-press', name: 'Leg Press (limited ROM)', sets: 4, reps: '8', load: 'RPE 6', tempo: '3-1-1', rest: '90s', notes: 'Stop at 60° knee flexion. Push both legs evenly.' },
          { exercise_id: 'ham-curl', name: 'Seated Hamstring Curl', sets: 3, reps: '10', tempo: '2-0-3', rest: '75s' },
          { exercise_id: 'step-up', name: 'Low Step-ups', sets: 3, reps: '8 each leg', notes: 'Control the descent — no knee cave.' },
          { exercise_id: 'balance', name: 'Single-leg Balance', sets: 3, reps: '30s each' },
        ],
      }
    }
    if (isJump) {
      return {
        label: `Day ${i + 1}`,
        focus: i === 0 ? 'Lower — Max Power' : i === 1 ? 'Upper + Trunk' : 'Speed + Plyo',
        exercises: [
          { exercise_id: 'warmup', name: 'Warm-up', reps: '8–10 min', is_warmup: true },
          { exercise_id: 'box-jump', name: 'Box Jumps', sets: 5, reps: '3', rest: '2 min', notes: 'Max intent. Reset every rep.' },
          { exercise_id: 'trap-bar-dl', name: 'Trap Bar Deadlift', sets: 4, reps: '3', load: '85% 1RM', rest: '3 min' },
          { exercise_id: 'bulgarian-split', name: 'Rear-Foot Elevated Split Squat', sets: 3, reps: '6 each', load: 'RPE 8', rest: '90s' },
          { exercise_id: 'depth-drop', name: 'Depth Drops', sets: 3, reps: '5', notes: 'Stick the landing — minimal ground time.' },
        ],
      }
    }
    return {
      label: `Day ${i + 1}`,
      focus: i === 0 ? 'Squat Focus' : i === 1 ? 'Hinge Focus' : 'Full Body',
      exercises: [
        { exercise_id: 'warmup', name: 'Warm-up', reps: '8 min', is_warmup: true },
        { exercise_id: 'back-squat', name: 'Back Squat', sets: 5, reps: '5', load: '80% 1RM', rest: '3 min' },
        { exercise_id: 'rdl', name: 'Romanian Deadlift', sets: 4, reps: '6', load: 'RPE 8', rest: '2 min' },
        { exercise_id: 'walking-lunge', name: 'Walking Lunges', sets: 3, reps: '10 each', rest: '90s' },
        { exercise_id: 'calf-raise', name: 'Standing Calf Raise', sets: 4, reps: '10', rest: '60s' },
      ],
    }
  }

  return {
    id: 'gen-' + Date.now(),
    name,
    description,
    goal_tags: tags,
    source: 'ai',
    weeks: [
      {
        week: 1,
        phase: isRehab ? 'REBUILD PHASE' : isJump ? 'POWER PHASE' : 'STRENGTH PHASE',
        note: isRehab ? 'Symmetry first. Do not chase load. Stop if pain > 3/10.' : 'Focus on quality of movement and intent.',
        days: Array.from({ length: dayCount }, (_, i) => dayTemplate(i)),
      },
    ],
  }
}

export const EXAMPLE_PROMPTS = [
  'ACL return-to-play, week 3, no deep knee flexion, 3 days/week',
  'Improve vertical jump for a basketball guard, 3 days, has a trap bar',
  'Off-season base strength, 4 days, main lifts',
]
